// "Approve update" — the one place the core-update offer, the approval and its
// outcome are rendered (task update_status_visible, ADR 0178).
//
// Two things this component exists to prevent, both of which shipped before it:
//
// 1. CLAIMING SUCCESS FROM A 202. What to say is decided by `updateBannerView`
//    (lib/updateBannerState.ts) from what the BOAT reported, never from the
//    POST's result. That module carries the reasoning and the tests.
//
// 2. BEING UNREACHABLE. The button used to live only on the multi-boat fleet
//    grid, and /dashboard redirects a single-boat account straight past it — so
//    most owners had no way to approve an update at all. One component,
//    rendered by both the fleet card and the boat chrome, keeps them in step.
//
// All this file owns is the POST, the poll, the dismissal, and the markup.
// Whether the close button appears at all is `updateBannerView`'s call too
// (`dismissible`), and where a dismissal is remembered is `updateBannerDismiss`.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../api';
import type { Boat } from '../../lib/useBoats';
import { dismissUpdateVersion, dismissedUpdateVersion } from '../../lib/updateBannerDismiss';
import { updateBannerView } from '../../lib/updateBannerState';

/** How often to re-read the boat while an approval is outstanding. */
const POLL_MS = 10_000;

/** The message from the API's error body, falling back to something readable. */
function apiErrorMessage(err: unknown): string {
  const body = (err as { body?: { message?: unknown } } | undefined)?.body;
  if (body && typeof body.message === 'string' && body.message.length > 0) return body.message;
  return 'The approval could not be sent. Try again.';
}

export interface BoatUpdateBannerProps {
  boat: Boat;
  /** Newer release this boat's runtime can install. Null = no actionable update. */
  coreLatest: string | null;
  /** Re-read GET /v1/boats — the banner polls this while it is waiting. */
  onRefresh: () => void;
}

export function BoatUpdateBanner({ boat, coreLatest, onRefresh }: BoatUpdateBannerProps) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The key the 202 handed back, plus when we started waiting on it.
  const [attempt, setAttempt] = useState<{ commandKey: string; startedAt: number } | null>(null);
  // Re-rendered on each poll so the wait limit is reached without a boat reply.
  const [now, setNow] = useState<number>(() => Date.now());
  // Read once per mount, as the other dismissible surfaces do: a dismissal made
  // in another tab should not make this banner vanish under a tap. The two
  // render sites are on different routes, so they are never both on screen.
  const [dismissedVersion, setDismissedVersion] = useState(() => dismissedUpdateVersion(boat.id));

  const view = updateBannerView({
    boatVersion: boat.core_version,
    coreLatest,
    lastUpdate: boat.lastUpdate,
    attempt,
    sending,
    error,
    dismissedVersion,
    enforced: boat.enforcementState === 'paused' || boat.enforcementState === 'follow_up',
    now,
  });

  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  // Poll only while an approval of ours is outstanding. The boat chrome already
  // runs a 30 s steady-state poll and the fleet grid runs none at all; this is
  // the one moment either surface needs to be prompt.
  const polling = attempt !== null && !view.settled && !view.timedOut;
  useEffect(() => {
    if (!polling) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
      refresh.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [polling]);

  const approve = useCallback(async () => {
    if (coreLatest === null) return;
    setSending(true);
    setError(null);
    try {
      const res = await api<{ status: string; idempotencyKey: string }>(
        `/v1/boats/${boat.id}/update/install`,
        { method: 'POST', body: { version: coreLatest } }
      );
      setAttempt({ commandKey: res.idempotencyKey, startedAt: Date.now() });
      setNow(Date.now());
      refresh.current();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  }, [boat.id, coreLatest]);

  const dismiss = useCallback(() => {
    if (coreLatest === null) return;
    dismissUpdateVersion(boat.id, coreLatest);
    setDismissedVersion(coreLatest);
  }, [boat.id, coreLatest]);

  if (!view.visible) return null;

  return (
    <div
      className={`update-banner${view.status?.tone === 'failed' ? ' update-banner--failed' : ''}`}
      data-testid={`update-banner-${boat.id}`}
    >
      <div className="update-banner-text">
        {view.offer ? <span className="update-banner-head">{view.offer}</span> : null}
        {view.status ? (
          <span className="update-banner-status" data-testid={`update-status-${boat.id}`}>
            {view.status.text}
          </span>
        ) : null}
      </div>
      {view.action ? (
        <button
          type="button"
          className="update-banner-action"
          disabled={view.action.disabled}
          onClick={() => void approve()}
          data-testid={`approve-update-${boat.id}`}
        >
          {view.action.label}
        </button>
      ) : null}
      {view.dismissible ? (
        <button
          type="button"
          className="update-banner-dismiss"
          onClick={dismiss}
          aria-label={`Dismiss the v${coreLatest ?? ''} update notice`}
          data-testid={`dismiss-update-${boat.id}`}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
